/**
 * DocumentGapView (P278.3, doc-track Phase B) -- the standing-start
 * answer on screen: per framework, which expected deliverables are
 * present / candidate / missing, from the P278.2 document mappings.
 */
import { DocumentReadingNote } from "shared/documents/DocumentReadingNote";
import { useState } from "react";
import { ConfirmedVerb } from "shared/components/ConfirmedVerb";
import { useTranslation } from "react-i18next";

import { useToast } from "shared/toast/ToastProvider";
import { Link } from "react-router-dom";

import {
  useConfirmCandidate,
  useRejectMapping,
  useCandidateDocuments,
  useDocumentGap,
} from "framework/api/useDocumentGap";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { StatusPill, type PillTone } from "shared/components/StatusPill";
import { ModuleRubric } from "shared/layout/ModuleHomeTile";
import type { DeliverableGap, FrameworkGapSummary, GapStatus } from "shared/api/types";

const STATUS_TONE: Record<GapStatus, PillTone> = {
  present: "green",
  candidate: "amber",
  missing: "red",
};

function CoverageBar({ summary }: { summary: FrameworkGapSummary }): JSX.Element {
  const { t } = useTranslation();
  const pct = Math.max(0, Math.min(100, summary.coverage_pct));
  return (
    <div data-testid={`gap-summary-${summary.framework_code}`} className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-muted">
          {summary.framework_code}
        </span>
        <span className="font-mono text-[11px] text-ink-2 tabular-nums">
          {t("modules.framework.documentGap.coverage", {
            present: summary.present,
            total: summary.total,
            pct,
          })}
        </span>
      </div>
      <span
        className="block h-[6px] w-full overflow-hidden rounded-full bg-paper-2"
        aria-hidden="true"
      >
        <span className="block h-full bg-brand-brass" style={{ width: `${pct}%` }} />
      </span>
      {summary.mandatory_missing > 0 && (
        <span className="text-[11px] text-rag-red">
          {t("modules.framework.documentGap.mandatoryMissing", {
            count: summary.mandatory_missing,
          })}
        </span>
      )}
    </div>
  );
}

function DeliverableRow({ row }: { row: DeliverableGap }): JSX.Element {
  const { t } = useTranslation();
  return (
    <tr
      data-testid={`gap-row-${row.deliverable_id}`}
      className="border-t border-paper-2"
    >
      <td className="py-1.5 pe-3 align-top">
        {/* Round 10 E1 -- the deliverable's name is the bank's or the
            regulator's, so it is isolated rather than left to take the
            page's direction. */}
        <bdi className="text-ink">{row.name}</bdi>
        {row.mandatory && (
          <>
            {/* ...and a separator in the TEXT. `ms-2` spaces it on
                screen and concatenates everywhere else: the walk read
                "IT Strategic RoadmapMANDATORY", which is no longer a
                right-to-left defect at all. */}
            {" · "}
            <span className="font-mono text-[10px] uppercase tracking-wide text-ink-muted">
              {t("modules.framework.documentGap.mandatory")}
            </span>
          </>
        )}
      </td>
      <td className="py-1.5 pe-3 align-top text-ink-2">{row.deliverable_type}</td>
      <td className="py-1.5 pe-3 align-top">
        <StatusPill tone={STATUS_TONE[row.status]}>
          {t(`modules.framework.documentGap.status.${row.status}`)}
        </StatusPill>
      </td>
      <td className="py-1.5 align-top">
        {/* ROUND 14 A3 -- every covering document, each with its own way
            back. The row named only the first: with four tied at
            confidence 1, the hostile one round 14 planted was neither
            named nor withdrawable from here. */}
        {row.covering_documents.length === 0 ? (
          <CandidateDocsFinder deliverableId={row.deliverable_id} name={row.name} />
        ) : (
          <>
            <ul className="space-y-0.5" data-testid={`gap-docs-${row.deliverable_id}`}>
              {row.covering_documents.map((covering) => (
                <CoveringDocumentLine key={covering.link_id} covering={covering} />
              ))}
            </ul>
            {/* Round 17 A30 -- once one document covered a deliverable
                the row offered no way to add a second. */}
            <CandidateDocsFinder
              deliverableId={row.deliverable_id}
              name={row.name}
              another
            />
          </>
        )}
      </td>
    </tr>
  );
}

function CoveringDocumentLine({
  covering,
}: {
  covering: DeliverableGap["covering_documents"][number];
}): JSX.Element {
  const { t } = useTranslation();
  const reject = useRejectMapping();
  const toast = useToast();
  const name = covering.title ?? covering.filename;
  return (
    <li>
      {/* C1 -- the covering link pointed at /twin/documents, which is
          not a registered route. The inventory lives at /admin/documents,
          and a preparer can read it. */}
      <Link
        to="/admin/documents"
        className="text-brand-brassDark hover:underline"
        data-testid={`gap-doc-${covering.link_id}`}
      >
        <bdi>{name}</bdi>
      </Link>
      <DocumentReadingNote
        reading={covering}
        testId={`gap-doc-reading-${covering.link_id}`}
      />
      {covering.link_status === "suggested" && (
        <span className="ms-2 text-[11px] text-rag-amber">
          ({t("modules.framework.documentGap.awaitingReview")})
        </span>
      )}
      {/* ROUND 13 A4 -- "This covers it" was one click with no way back,
          and a confirmed-mapped document is refused deletion by design,
          so this is the containment a bank has once it discovers a bad
          document. A separator in the TEXT (round 12 H5), not a margin. */}
      {" · "}
      <ConfirmedVerb
        testId={`gap-reject-${covering.link_id}`}
        disabled={reject.isPending}
        label={t("modules.framework.documentGap.rejectCovers")}
        prompt={t("modules.framework.documentGap.rejectCoversAsk", { name })}
        confirmLabel={t("modules.framework.documentGap.rejectCoversConfirm")}
        onConfirm={() => {
          reject.mutate(
            { linkId: covering.link_id },
            {
              onSuccess: () =>
                toast.push({
                  tone: "success",
                  message: t("modules.framework.documentGap.rejected", { name }),
                }),
            },
          );
        }}
      />
    </li>
  );
}

/** P286.3: for an uncovered deliverable, lazily rank held documents. */
function CandidateDocsFinder({
  deliverableId,
  name,
  another = false,
}: {
  deliverableId: string;
  name: string;
  /** Round 17 A30 -- offered beside documents that already cover it. */
  another?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const candidates = useCandidateDocuments(deliverableId, open);
  const confirm = useConfirmCandidate();

  if (!open) {
    return (
      <div>
        {!another && (
          <span className="text-ink-muted">
            {t("modules.framework.documentGap.noDocument")}
          </span>
        )}
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={
            another
              ? `gap-add-another-${deliverableId}`
              : `gap-find-docs-${deliverableId}`
          }
          className={`${another ? "" : "ms-2 "}text-[11px] text-brand-brassDark hover:underline`}
        >
          {another
            ? t("modules.framework.documentGap.addAnother")
            : t("modules.framework.documentGap.findCandidates")}
        </button>
      </div>
    );
  }

  const docs = candidates.data?.documents ?? [];
  return (
    <div data-testid={`gap-candidates-${deliverableId}`} className="space-y-1">
      <span className="font-mono text-[10px] uppercase tracking-wide text-ink-muted">
        {t("modules.framework.documentGap.candidatesFor", { name })}
      </span>
      {candidates.isError && (
        <p className="text-[11px] text-rag-red">
          {t("modules.framework.documentGap.candidatesError")}
        </p>
      )}
      {candidates.isSuccess && docs.length === 0 && (
        <p className="text-[11px] text-ink-muted">
          {t("modules.framework.documentGap.candidatesNone")}
        </p>
      )}
      <ul className="space-y-0.5">
        {docs.map((d) => (
          <li key={d.document_id} className="flex items-center gap-2 text-[12px]">
            <Link
              to="/admin/documents"
              className="text-brand-brassDark hover:underline"
            >
              {d.title ?? d.filename}
            </Link>
            {d.withdrawn && (
              <span
                data-testid={`gap-withdrawn-${d.document_id}`}
                className="text-[10px] text-amber-800"
              >
                {t("modules.framework.documentGap.withdrawn")}
              </span>
            )}
            {/* Round 18 A35 -- the percentage was unexplained: it is how
                closely the document's name matches the deliverable's,
                not how much of it the document covers. */}
            <span
              className="font-mono text-[10px] text-ink-muted"
              title={t("modules.framework.documentGap.similarityWhy")}
            >
              {t("modules.framework.documentGap.similarity", {
                pct: Math.round(d.similarity * 100),
              })}
            </span>
            {/* B1 -- the list offered a percentage and no verb, so a
                preparer who could SEE the right document had no way to
                say so, and Test of Design stayed a dead end for three
                rounds. The endpoint has admitted a preparer since the
                mapping shipped; only the button was missing. */}
            <button
              type="button"
              data-testid={`gap-confirm-${d.document_id}`}
              disabled={confirm.isPending}
              onClick={() =>
                confirm.mutate(
                  { documentId: d.document_id, deliverableId },
                  {
                    // Round 9 E6 -- the row re-sorted and the button
                    // went away. Confirming a mapping is what makes
                    // Test of Design able to read that policy, which
                    // is a consequence two screens away and not one
                    // the list can show.
                    onSuccess: () =>
                      toast.push({
                        tone: "success",
                        message: t("modules.framework.documentGap.confirmed", {
                          name: d.title || d.filename,
                        }),
                      }),
                  },
                )
              }
              className="rounded border border-brand-brass/40 bg-brand-brass/5 px-1.5 py-0.5 text-[10px] font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
            >
              {t("modules.framework.documentGap.confirmCovers")}
            </button>
          </li>
        ))}
      </ul>
      {confirm.isError && (
        <p className="text-[11px] text-rag-red">{confirm.error.message}</p>
      )}
    </div>
  );
}

export function DocumentGapView(): JSX.Element {
  const { t } = useTranslation();
  const [framework, setFramework] = useState<string | null>(null);
  const gap = useDocumentGap(framework);

  const frameworks = gap.data
    ? [...new Set(gap.data.deliverables.map((d) => d.framework_code))].sort()
    : [];

  return (
    <article data-testid="document-gap-view" className="max-w-5xl space-y-5">
      <header className="space-y-1">
        <ModuleRubric>{t("modules.framework.documentGap.rubric")}</ModuleRubric>
        <h1 className="font-display text-2xl text-ink">
          <MarsadIcon
            name="document-locker"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("modules.framework.documentGap.heading")}
        </h1>
        <p className="max-w-2xl text-sm text-ink-2">
          {t("modules.framework.documentGap.intro")}
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setFramework(null)}
          data-testid="gap-framework-all"
          className={`rounded-full border px-3 py-1 text-sm ${
            framework === null
              ? "border-brand-brass bg-brand-brass/10 text-brand-brassDark"
              : "border-paper-2 text-ink-2 hover:bg-paper"
          }`}
        >
          {t("modules.framework.documentGap.allFrameworks")}
        </button>
        {frameworks.map((fw) => (
          <button
            key={fw}
            type="button"
            onClick={() => setFramework(fw)}
            data-testid={`gap-framework-${fw}`}
            className={`rounded-full border px-3 py-1 font-mono text-xs ${
              framework === fw
                ? "border-brand-brass bg-brand-brass/10 text-brand-brassDark"
                : "border-paper-2 text-ink-2 hover:bg-paper"
            }`}
          >
            {fw}
          </button>
        ))}
      </div>

      {gap.isError && (
        <p data-testid="gap-error" className="text-sm text-rag-red">
          {t("modules.framework.documentGap.error")}
        </p>
      )}

      {gap.data && gap.data.deliverables.length === 0 && (
        <p data-testid="gap-empty" className="text-sm text-ink-2">
          {t("modules.framework.documentGap.empty")}
        </p>
      )}

      {gap.data && gap.data.summaries.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {gap.data.summaries.map((s) => (
            <CoverageBar key={s.framework_code} summary={s} />
          ))}
        </div>
      )}

      {gap.data && gap.data.deliverables.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-start font-mono text-[11px] uppercase tracking-wide text-ink-muted">
                <th className="pb-1 pe-3 text-start">
                  {t("modules.framework.documentGap.col.deliverable")}
                </th>
                <th className="pb-1 pe-3 text-start">
                  {t("modules.framework.documentGap.col.type")}
                </th>
                <th className="pb-1 pe-3 text-start">
                  {t("modules.framework.documentGap.col.status")}
                </th>
                <th className="pb-1 text-start">
                  {t("modules.framework.documentGap.col.covering")}
                </th>
              </tr>
            </thead>
            <tbody>
              {gap.data.deliverables.map((row) => (
                <DeliverableRow key={row.deliverable_id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}
